/**
 * ReassignSeriesDialog
 * Umverteilt alle zukuenftigen (oder alle) Einsaetze einer Serie auf einen
 * anderen Mitarbeiter. Tage mit Konflikten beim neuen Mitarbeiter werden
 * automatisch uebersprungen.
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Loader2, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { shiftsAPI } from '../../lib/api';
import { formatLastFirst, getDuplicateNameKeys } from '../../lib/nameFormat';

export const ReassignSeriesDialog = ({
  open,
  onOpenChange,
  seriesId,
  currentEmployeeId,
  employees,
  onDone,
}) => {
  const [newEmployeeId, setNewEmployeeId] = useState('');
  const [onlyFuture, setOnlyFuture] = useState(true);
  const [saving, setSaving] = useState(false);

  const available = (employees || []).filter((e) => e.id !== currentEmployeeId);

  const handleSubmit = async () => {
    if (!newEmployeeId) {
      toast.error('Bitte neuen Mitarbeiter auswählen');
      return;
    }
    setSaving(true);
    try {
      const res = await shiftsAPI.reassignSeries(seriesId, newEmployeeId, onlyFuture);
      const { reassigned, skipped } = res.data || {};
      if (reassigned === 0) {
        toast.warning('Keine Einsätze verschoben — alle Tage haben bereits einen Einsatz beim neuen Mitarbeiter');
      } else if (skipped > 0) {
        toast.success(`${reassigned} Einsätze umverteilt, ${skipped} Tage übersprungen (bereits belegt)`);
      } else {
        toast.success(`${reassigned} Einsätze umverteilt`);
      }
      onOpenChange(false);
      onDone?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Umverteilen');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md w-[calc(100vw-1rem)] sm:w-full p-4 sm:p-6 max-h-[92vh] overflow-y-auto" data-testid="reassign-series-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="h-5 w-5" />
            Serie umverteilen
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Neuer Mitarbeiter *</Label>
            <Select value={newEmployeeId} onValueChange={setNewEmployeeId}>
              <SelectTrigger data-testid="reassign-employee-select">
                <SelectValue placeholder="Mitarbeiter auswählen" />
              </SelectTrigger>
              <SelectContent>
                {(() => {
                  const dk = getDuplicateNameKeys(available);
                  return available.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {formatLastFirst(e, dk)}
                    </SelectItem>
                  ));
                })()}
              </SelectContent>
            </Select>
          </div>

          <label className="flex items-start gap-2 cursor-pointer text-sm">
            <Checkbox
              checked={onlyFuture}
              onCheckedChange={(v) => setOnlyFuture(Boolean(v))}
              data-testid="reassign-only-future"
            />
            <span>
              <span className="font-medium">Nur zukünftige Einsätze</span>
              <span className="block text-xs text-muted-foreground">
                Empfohlen — vergangene Einsätze bleiben dem bisherigen Mitarbeiter für die Nachvollziehbarkeit zugeordnet.
              </span>
            </span>
          </label>

          <p className="text-xs text-muted-foreground bg-secondary/40 rounded-md p-2">
            Tage, an denen der neue Mitarbeiter schon einen Einsatz hat, werden übersprungen.
          </p>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">Abbrechen</Button>
          <Button
            onClick={handleSubmit}
            disabled={saving || !newEmployeeId}
            data-testid="reassign-submit-btn"
            className="w-full sm:w-auto"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserCog className="h-4 w-4 mr-2" />}
            Umverteilen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReassignSeriesDialog;
